import React, { Suspense, ComponentType } from 'react';
import LoadingSpinner from '../components/UI/LoadingSpinner';

/**
 * Utility for creating lazy-loaded components with consistent loading states
 */

interface LazyLoadOptions {
  fallback?: React.ComponentType;
  errorBoundary?: boolean;
  chunkName?: string;
}

/**
 * Creates a lazy-loaded component with loading fallback
 */
export function createLazyComponent<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  options: LazyLoadOptions = {}
): ComponentType<P> {
  const LazyComponent = React.lazy(importFn);

  const {
    fallback: FallbackComponent = LoadingSpinner
  } = options;

  return (props: P) => (
    <Suspense fallback={<FallbackComponent />}>
      <LazyComponent {...(props as any)} />
    </Suspense>
  );
}

/**
 * Page-specific loading component
 */
export const PageLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="lg" />
  </div>
);

/**
 * Route-specific loading component
 */
export const RouteLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <LoadingSpinner size="md" />
  </div>
);

/**
 * Component-specific loading component
 */
export const ComponentLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center p-4">
    <LoadingSpinner size="sm" />
  </div>
);

/**
 * Creates a lazy route component with page-level loading
 */
export function createLazyRoute<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>
): ComponentType<P> {
  return createLazyComponent(importFn, {
    fallback: PageLoadingSpinner
  });
}

/**
 * Creates a lazy component with component-level loading
 */
export function createLazyWidget<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>
): ComponentType<P> {
  return createLazyComponent(importFn, {
    fallback: ComponentLoadingSpinner
  });
}

/**
 * Preload a lazy component
 */
export function preloadComponent<P = {}>(
  importFn: () => Promise<{ default: ComponentType<P> }>
): Promise<void> {
  return importFn().then(() => void 0);
}

/**
 * Batch preload multiple components
 */
export function preloadComponents(
  importFns: Array<() => Promise<{ default: ComponentType<any> }>>
): Promise<void[]> {
  return Promise.all(importFns.map(preloadComponent));
}